import {
  HyperlinkStaticCellRenderer,
  IGridHyperlink,
} from '../../../../utils/custom-renderer/hyperlink-cell-renderer';
import { CONTRACT_TYPE, ICONS } from '../../../../utils';
import { AnalyticsStepTwo } from './analytics-step-two';
import { CASCADE_ENUM } from '../../../../services/enum.service';
import {
  getFillColorFromProjectStatus,
  getFormattedDate,
  getFormattedNumber,
  getStatusColour,
  getStatusTranslate,
  getPriceValue,
  getCreditProfileValue, DEFAULT_REC_SETTLEMENT_POINT
} from '../../../../utils/formatting';
import {
  ActionCellRenderer,
  ActionTypes,
} from '../../../../utils/custom-renderer/action-cell-renderer';
import { getTechnologyIconName, selectedEyeIcon } from '../../../../styles';
import Highcharts from 'highcharts';
import { CUSTOM_HEADER_TOOLTIPS, headerWithTooltipColParams } from '../../../../utils/grid';

export enum COLUMN_FIELDS {
  OPERATIONAL_STATUS = 'OPERATIONAL_STATUS',
  PROJECT_NAME = 'PROJECT_NAME',
  TECHNOLOGY = 'TECHNOLOGY',
  ISO = 'ISO',
  TICKER = 'TICKER',
  UNIQUE_ID = 'UNIQUE_ID',
  SETTLEMENT_POINT = 'SETTLEMENT_POINT',
  OFFERED_CAPACITY = 'OFFERED_CAPACITY',
  FIXED_PRICE = 'FIXED_PRICE',
  TENOR_START_DATE = 'TENOR_START_DATE',
  TENOR_MONTHS = 'TENOR_MONTHS',
  SELLER_POST_COD_COLLATERAL = 'SELLER_POST_COD_COLLATERAL',
  VPPA_STATUS = 'VPPA_STATUS',
  ACTIONS = 'ACTIONS',
  REFERENCED_EXPECTED_SETTLEMENT = 'REFERENCED_EXPECTED_SETTLEMENT',
  REFERENCED_IMPLIED_CARBON_PRICE = 'REFERENCED_IMPLIED_CARBON_PRICE',
  REFERENCED_LME = 'REFERENCED_LME',
}

function getStatusNameStyle(params: any) {
  const response = {};
  if (params.data?.VPPA_STATUS) {
    response['color'] = getStatusColour(params.data?.VPPA_STATUS);
  }

  return response;
}

function getActions(context: AnalyticsStepTwo, params): Array<any> {
  let actions = [];

  const selectedButton = {
    name: selectedEyeIcon(),
    callback: () => {
      params.node.setSelected(!params.node.isSelected());
      context.buyerAnalyticsService.stepTwoSelectedVppas =
        context.vppaDrilldownGrid.gridApi.getSelectedRows();
      context.generateGraphs(
        context.buyerAnalyticsService.stepTwoSelectedVppas,
        context.timePeriod,
      );
    },
    type: ActionTypes.DESELECT,
    actionName: '',
  };
  actions = actions.concat(...[selectedButton]);

  return actions;
}

export function VPPA_COLUMNS(context: AnalyticsStepTwo) {
  return [
    {
      suppressMenu: true,
      maxWidth: 0,
      lockPosition: 'left',
      lockPinned: true,
      checkboxSelection: (params) => {
        if (context.selectedVppaRankings.find((elm) => elm.PROJECT_ID === params.data.PROJECT_ID)) {
          params.node.setSelected(true);
        }
      },
    },
    {
      field: COLUMN_FIELDS.ACTIONS,
      headerName: '',
      suppressMenu: true,
      maxWidth: 50,
      lockPosition: 'left',
      lockPinned: true,
      cellRenderer: ActionCellRenderer,
      cellRendererParams: (params) => {
        const actions = getActions(context, params);
        const enabled = true;
        return {
          actions,
          enabled,
        };
      },
    },
    {
      field: COLUMN_FIELDS.PROJECT_NAME,
      headerName: 'Project',
      suppressMenu: true,
      lockPosition: 'left',
      lockPinned: true,
      flex: 1,
      sortable: false,
      cellStyle: {
        color: '#a9ff78',
      },
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        let data;
        const iconList: Array<{ icon: ICONS; width; height; fill? }> = [];

        iconList.push({
          icon: ICONS.CIRCLE,
          width: '14px',
          height: '14px',
          fill: Highcharts.getOptions().colors[params.node.rowIndex],
        });

        iconList.push({
          icon: getTechnologyIconName(params.data.TECHNOLOGY),
          width: '12px',
          height: '13px',
          fill: getFillColorFromProjectStatus(params.data.PROJECT_OPERATIONAL_STATUS),
        });

        data = JSON.stringify({
          NAME: params.data.PROJECT_NAME,
          PROJECT_ID: params.data.PROJECT_ID,
          ICON_LIST: iconList,
        });

        const obj = JSON.parse(data);

        if (!obj.NAME) {
          const hyperlinkParams: IGridHyperlink = {
            value: null,
            id: null,
            vppaId: null,
            iconList: [],
          };
          return hyperlinkParams;
        }

        const hyperlinkParams: IGridHyperlink = {
          value: obj.NAME,
          id: obj.PROJECT_ID,
          vppaId: null,
          iconList: obj.ICON_LIST,
        };
        return hyperlinkParams;
      },
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.ICON_LEGEND),
    },
    {
      field: COLUMN_FIELDS.TECHNOLOGY,
      headerName: 'Technology',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      flex: 1,
      valueFormatter: (params) => {
        if (params.data) {
          return context.enumService.getEnumTranslation(
            CASCADE_ENUM.TECHNOLOGY,
            params.data.TECHNOLOGY,
          );
        }
      },
    },
    {
      field: COLUMN_FIELDS.ISO,
      headerName: 'ISO',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.TICKER,
      headerName: 'Ticker',
      menuTabs: ['filterMenuTab'],
      filter: 'agTextColumnFilter',
      lockPinned: true,
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.UNIQUE_ID,
      headerName: 'Unique ID',
      menuTabs: ['filterMenuTab'],
      filter: 'agTextColumnFilter',
      lockPinned: true,
      flex: 1,
      valueGetter: (params) => {
        return JSON.stringify({
          NAME: params.data.UNIQUE_ID,
          PROJECT_ID: params.data.PROJECT_ID,
        });
      },
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        const obj = JSON.parse(params.value);
        const hyperlinkParams: IGridHyperlink = {
          value: obj.NAME,
          id: obj.PROJECT_ID,
          vppaId: params.data?.WATCHLIST_ENTITY_ID,
          iconList: [],
        };
        return hyperlinkParams;
      },
    },
    {
      field: COLUMN_FIELDS.SETTLEMENT_POINT,
      headerName: 'Settlement Point',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      flex: 1,
      filterValueGetter: (params) => (params.data?.CONTRACT_TYPE === CONTRACT_TYPE.REC) ? DEFAULT_REC_SETTLEMENT_POINT : params.data?.SETTLEMENT_POINT,
      valueFormatter: (params) => (params.data?.CONTRACT_TYPE === CONTRACT_TYPE.REC) ? DEFAULT_REC_SETTLEMENT_POINT : params.data?.SETTLEMENT_POINT,
    },
    {
      field: COLUMN_FIELDS.OFFERED_CAPACITY,
      headerName: 'Offered Capacity (MW)',
      menuTabs: ['filterMenuTab'],
      filter: 'agTextColumnFilter',
      lockPinned: true,
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.FIXED_PRICE,
      headerName: 'Fixed Price ($/MWh)',
      menuTabs: ['filterMenuTab'],
      filter: 'agTextColumnFilter',
      filterValueGetter: (props) => getPriceValue(props.data.FIXED_PRICE),
      lockPinned: true,
      flex: 1,
      valueFormatter: (props) => getPriceValue(props.data.FIXED_PRICE, '', '$'),
    },
    {
      field: COLUMN_FIELDS.TENOR_START_DATE,
      headerName: 'Tenor Start Date',
      menuTabs: ['filterMenuTab'],
      filter: 'agTextColumnFilter',
      lockPinned: true,
      flex: 1,
      filterValueGetter: (params) => getFormattedDate(params.data.TENOR_START_DATE),
      valueFormatter: (params) => getFormattedDate(params.data.TENOR_START_DATE),
    },
    {
      field: COLUMN_FIELDS.TENOR_MONTHS,
      headerName: 'Tenor',
      menuTabs: ['filterMenuTab'],
      filter: 'agTextColumnFilter',
      lockPinned: true,
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.SELLER_POST_COD_COLLATERAL,
      headerName: 'Credit Profile ($/MW)',
      menuTabs: ['filterMenuTab'],
      filter: 'agTextColumnFilter',
      lockPinned: true,
      flex: 1,
      valueFormatter: (params) =>
        getCreditProfileValue(
          params.data.VPPA_CREDIT_PROFILE,
          params.data.SELLER_POST_COD_COLLATERAL,
        ),
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.CREDIT_PROFILE),
    },
    {
      field: COLUMN_FIELDS.REFERENCED_EXPECTED_SETTLEMENT,
      headerName: 'Expected Settlement ($/MWh)',
      menuTabs: ['filterMenuTab'],
      filter: 'agTextColumnFilter',
      lockPinned: true,
      flex: 1,
      filterValueGetter: (props) => getPriceValue(props.data.REFERENCED_EXPECTED_SETTLEMENT),
      valueFormatter: (props) => getPriceValue(props.data.REFERENCED_EXPECTED_SETTLEMENT, '', '$'),
    },
    {
      field: COLUMN_FIELDS.REFERENCED_IMPLIED_CARBON_PRICE,
      headerName: 'Implied Carbon Price ($/MT CO₂)',
      menuTabs: ['filterMenuTab'],
      filter: 'agTextColumnFilter',
      lockPinned: true,
      flex: 1,
      filterValueGetter: (props) => getPriceValue(props.data.REFERENCED_IMPLIED_CARBON_PRICE),
      valueFormatter: (props) => getPriceValue(props.data.REFERENCED_IMPLIED_CARBON_PRICE, '', '$'),
    },
    {
      field: COLUMN_FIELDS.REFERENCED_LME,
      headerName: 'Nodal LME (MT CO₂/MWh)',
      menuTabs: ['filterMenuTab'],
      filter: 'agTextColumnFilter',
      lockPinned: true,
      flex: 1,
      filterValueGetter: (props) => getFormattedNumber(props.data.REFERENCED_LME, '', 2),
      valueFormatter: (props) => getFormattedNumber(props.data.REFERENCED_LME, '', 2),
    },
    {
      field: COLUMN_FIELDS.VPPA_STATUS,
      headerName: 'Status',
      menuTabs: ['filterMenuTab'],
      lockPinned: true,
      flex: 1,
      cellStyle: (params) => getStatusNameStyle(params),
      valueFormatter: (params) => getStatusTranslate(params.data.VPPA_STATUS),
      filterValueGetter: (params) => getStatusTranslate(params.data.VPPA_STATUS),
    },
  ];
}
